import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../storeHook';
import { useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import { showPopup } from '../../services/features/popupSlice';

export const useAuthHelper = () => {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const authUser = useAppSelector(
    (state) => state.authUser
  );
  const { title } = useAppSelector(
    (state) => state?.refresh
  );

  const checkIfRolePresent = (role: string) =>
    authUser?.role?.includes(role);

  const checkPageAccess = (roles: string[] | string) =>
    !!(
      checkIfRolePresent('ADMIN') ||
      (typeof roles === 'string'
        ? checkIfRolePresent(roles as string)
        : roles.find((role) => checkIfRolePresent(role)))
    );

  const refreshToken = async () => {
    try {
      if (!instance) {
        console.error('MSAL instance is not available');
        return;
      }

      // Ensure MSAL is initialized
      await instance.initialize();

      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: ['User.Read'],
        });

        if (response && response.accessToken) {
          localStorage.setItem(
            'authToken',
            JSON.stringify(response.accessToken)
          );
        }
      } else {
        throw new Error('No active account found');
      }
    } catch (error: any) {
      console.error('Token refresh failed:', error);

      if (
        error.errorCode === 'interaction_required' ||
        error.errorCode === 'invalid_grant'
      ) {
        try {
          const loginResponse =
            await instance.acquireTokenPopup({
              scopes: ['User.Read'],
            });

          if (loginResponse && loginResponse.accessToken) {
            localStorage.setItem(
              'authToken',
              loginResponse.accessToken
            );
          }
        } catch (popupError) {
          console.error('Popup login failed:', popupError);
          dispatch(
            showPopup({
              title: 'Unauthorized User, Session Expired',
              desc: 'Please login to continue.',
            })
          );
        }
      } else {
        dispatch(
          showPopup({
            title: 'Unauthorized User, Session Expired',
            desc: 'Please login to continue.',
          })
        );
      }
    }
  };

  useEffect(() => {
    if (title) {
      refreshToken();
    }
  }, [title]);

  return {
    checkIfRolePresent,
    checkPageAccess,
    refreshToken,
  };
};
